import {EventType, type Message, type State} from '@ag-ui/core';
import {
    type PageChangeSetStatusItem,
    type WorkspaceContextUploadStatusItemEvent,
    WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
    chatStatusItemEventFromUnknown,
    isPageChangeSetStatusItem,
    workspaceContextUploadStatus,
} from './chat-status';
import {ChatStateStore, type ChatAgUiEvent, type ChatSnapshot} from './chat-state';
import {ChatUi} from './chat-ui';
import {fetchChatbotBootstrap, fetchChatbotRestartBootstrap, rememberChatbotRestartSource} from './chatbot-restart';
import type {PageChangeSetActions} from './chat-ui/change-set-cards';

declare const real: any;
declare const howspace: any;

// @ag-ui/client is used here for protocol types only. The Howspace chatbot does
// not instantiate AG-UI's HTTP/SSE transport: HTTP queues the run, the websocket
// topic delivers AG-UI events, and ChatStateStore is our reducer for those events.
// SSE remains a debug/test transport outside the normal Howspace UI.

export type ChatbotBootstrap = {
    /** Stable server-side chatbot id backing this UI. */
    chatbotId: string;
    /** Stable server-side agent id used by the AG-UI run protocol. */
    agentId: string;
    /** Stable server-side thread type identifier. */
    threadTypeId: string;
    /** Current thread id to continue. */
    threadId: string;
    /** Number of persisted AG-UI events the browser has replayed for this thread. */
    threadRevision: number;
    /** HTTP endpoint that queues one assistant turn. */
    runUrl: string;
    /** Endpoint that archives the current thread and returns a fresh bootstrap. */
    newThreadUrl: string;
    /** Endpoint that returns a fresh bootstrap of this thread, for catching up after a reconnect. */
    bootstrapUrl: string;
    /** Whether a run was in progress when the bootstrap was taken. Its events arrive only when it ends. */
    runInProgress: boolean;
    runHardCapSeconds: number;
    /** Replayable AG-UI event history for the thread. */
    events: ChatAgUiEvent[];
    /** Agent state reconstructed from STATE_SNAPSHOT events, or schema-backed server memory when configured. */
    state: Record<string, unknown>;
    /** Metadata echoed through AG-UI's forwardedProps field on each run request. */
    runMetadata: Record<string, unknown>;
    /** Websocket topic used for async AG-UI run events. */
    agUiTopic?: string;
    /** Optional one-connection websocket subscription token for the AG-UI topic. */
    agUiTopicToken?: string;
    model: {
        mismatch: boolean;
    };
    labels: {
        placeholder: string;
        send: string;
        emptyState: string;
        thinking: string;
        thoughtFor: string;
        fewSeconds: string;
        seconds: string;
        copy: string;
        close: string;
        expandStatus: string;
        collapseStatus: string;
        toolCalls: string;
        toolStatusRunning: string;
        toolStatusDone: string;
        toolStatusError: string;
        toolArguments: string;
        toolResult: string;
        modelChangedWarning: string;
        threadStaleWarning: string;
        runTimeoutWarning: string;
        inputContextTooLargeWarning: string;
        queueDelayNotice: string;
        runStillQueuedNotice: string;
        thinkingWords: string;
        stillWorking: string;
        connectionFailed: string;
        error: string;
        tryAgain: string;
        addContextFile: string;
        contextFileQueued: string;
        contextFileProcessing: string;
        contextFileReady: string;
        contextFileFailed: string;
        contextFileRemove: string;
        contextFileRemoveConfirm: string;
        contextFileRemoveConfirmAction: string;
        contextFileKeep: string;
        contextFileRemoveFailed: string;
        contextFileGeneratePageAction: string;
        contextFileCreateWidgetAction: string;
        contextFileMaxSourceFileSize: string;
        contextFileMaxCreatedAssetCount: string;
        contextFileMaxCreatedAssetFileSize: string;
        composerNote: string;
        emptyStateDescription: string;
    };
    features: {
        showReasoning: boolean;
        showToolCalls: boolean;
        showFriendlyToolStatus: boolean;
        workspaceContextUpload: boolean;
        contextFileQuickActions: boolean;
    };
    toolLabels: Record<string, string>;
    openingQuestion?: string;
    /** Why sending is paused because the organization's AI credits ran out. Empty while AI is available. */
    aiCreditsExhaustedNotice?: string;
    workspaceContextFiles?: Record<string, unknown>[];
    /** Change sets of this thread that wait for the user or are being applied. */
    pageChangeSets?: Record<string, unknown>[];
    pageChangeSetLabels?: Record<string, string>;
    /** Base of the endpoints the change set card's buttons call. Empty when the chat stages no changes. */
    pageChangeSetUrl?: string;
};

export type ChatbotInstance = {
    agent: ChatbotAgent;
    store: ChatStateStore;
    ui: ChatUi;
    container: HTMLElement;
    bootstrap: ChatbotBootstrap;
    unsubscribeAgUi: (() => void) | null;
    unsubscribeStatusItems: (() => void) | null;
    waitingForWorkerRunId: string | null;
    runWatchdog: number | null;
    /** What to put back if the run this client started never reports. Null once it has, or after a reload. */
    pendingRun: PendingRun | null;
};

/** The chat as it was before a send, kept until the run it started is known to have been stored. */
type PendingRun = {
    snapshot: ChatSnapshot;
    threadRevision: number;
    content: string;
};

const instances = new WeakMap<HTMLElement, ChatbotInstance>();
/** Labels of the chat a container last showed, for the loading state while it is fetched again. */
const restartLabels = new WeakMap<HTMLElement, ChatbotBootstrap['labels']>();
/** Client context belongs to the host, including before initialization and across thread refreshes. */
const runMetadataOverrides = new WeakMap<HTMLElement, Record<string, unknown>>();
const AG_UI_EVENT_ACTION = 'ai:ag-ui:event';
const AI_CREDITS_EXHAUSTED_ERROR_CODE = 'ai_credits_exhausted';
const CHAT_STATUS_ITEM_ACTION = 'ai:chatbot:status-item';

type ChatbotAgent = {
    isRunning: boolean;
    state: State;
    abortRun(): void;
};

export const readChatbotBootstrap = (root: HTMLElement): ChatbotBootstrap | null => {
    const json = root.dataset.chatbotBootstrap;
    if (!json) {
        return null;
    }

    try {
        return normalizeBootstrap(JSON.parse(json) as Partial<ChatbotBootstrap>);
    } catch (error) {
        console.error('AI chatbot: invalid bootstrap data', error);
        return null;
    }
};

/**
 * The bootstrap rendered into the container, or a fresh one when the chat was shown in it before and destroyed.
 * Null when there is neither.
 */
export const loadChatbotBootstrap = async (container: HTMLElement): Promise<ChatbotBootstrap | null> => {
    const inlineBootstrap = readChatbotBootstrap(container);
    if (inlineBootstrap !== null) {
        return inlineBootstrap;
    }

    const labels = restartLabels.get(container);
    const loadingIndicator = labels ? showConnectionLoading(container, labels) : null;
    try {
        const payload = await fetchChatbotRestartBootstrap(container, websocketIdHeader());
        if (payload === null) {
            loadingIndicator?.remove();
            return null;
        }

        const bootstrap = normalizeBootstrap(payload as Partial<ChatbotBootstrap>);
        bootstrap.labels = labels ? mergeLabels(labels, bootstrap.labels) : bootstrap.labels;
        return bootstrap;
    } catch (error) {
        if (loadingIndicator && labels) {
            showConnectionError(loadingIndicator, labels);
        }
        throw error;
    }
};

export const initChatbot = async (
    hostElement: HTMLElement,
    container: HTMLElement,
    bootstrap: ChatbotBootstrap,
): Promise<ChatbotInstance> => {
    bootstrap = normalizeBootstrap(bootstrap);
    const loadingIndicator = showConnectionLoading(container, bootstrap.labels);

    try {
        await subscribeToAgUiTopic(bootstrap.agUiTopic, bootstrap.agUiTopicToken);
    } catch (error) {
        console.error('AI chatbot: websocket subscription failed', error);
        showConnectionError(loadingIndicator, bootstrap.labels);
        throw error;
    }

    loadingIndicator.remove();
    bootstrap.runMetadata = {...bootstrap.runMetadata, ...runMetadataOverrides.get(hostElement)};

    const store = new ChatStateStore(bootstrap.events || []);
    store.setState(bootstrap.state);
    if (bootstrap.runInProgress) {
        store.markRunInProgress();
    }
    const agent = createAgent(bootstrap);
    const ui = new ChatUi(container, {
        labels: bootstrap.labels,
        showReasoning: bootstrap.features.showReasoning,
        showToolCalls: bootstrap.features.showToolCalls,
        showFriendlyToolStatus: bootstrap.features.showFriendlyToolStatus,
        toolLabels: bootstrap.toolLabels,
        modelMismatch: bootstrap.model.mismatch,
        workspaceContextUpload: bootstrap.features.workspaceContextUpload,
        contextFileQuickActions: bootstrap.features.contextFileQuickActions,
        openingQuestion: bootstrap.openingQuestion,
        pausedNotice: bootstrap.aiCreditsExhaustedNotice,
        onSubmit: message => {
            void runMessage(hostElement, message);
        },
        onAddContextFile: file => {
            openWorkspaceContextFileModal(hostElement, file);
        },
        onRemoveContextFile: contextFileId => removeWorkspaceContextFile(contextFileId),
        changeSets: bootstrap.pageChangeSetUrl
            ? {
                labels: bootstrap.pageChangeSetLabels || {},
                actions: pageChangeSetActions(
                    hostElement,
                    bootstrap.pageChangeSetUrl,
                    bootstrap.pageChangeSetLabels || {},
                ),
                currentPageId: () => currentPageIdOf(bootstrap.runMetadata),
                previewShown: () => {
                    const sidebar = hostElement.closest('#ai-sidebar');
                    return sidebar === null || sidebar.classList.contains('expanded');
                },
            }
            : undefined,
    });

    for (const changeSet of bootstrap.pageChangeSets || []) {
        const event = chatStatusItemEventFromUnknown(changeSet);
        if (event !== null) {
            store.applyStatusItemEvent(event);
        }
    }

    ui.renderInitial(store.snapshot());
    delete container.dataset.chatbotBootstrap;

    const instance: ChatbotInstance = {
        agent,
        store,
        ui,
        container,
        bootstrap,
        unsubscribeAgUi: null,
        unsubscribeStatusItems: null,
        waitingForWorkerRunId: null,
        runWatchdog: null,
        pendingRun: null,
    };
    instance.unsubscribeAgUi = listenToAgUiEvents(instance);
    instance.unsubscribeStatusItems = listenToStatusItemUpdates(instance);
    instances.set(hostElement, instance);
    syncRunWatchdog(instance);
    armReconnectRefresh(hostElement);

    for (const contextFile of bootstrap.workspaceContextFiles || []) {
        applyWorkspaceContextUploadStatus(instance, contextFile);
    }

    return instance;
};

/** Hosts whose reconnect handler is registered. The handler outlives the instance, so it is registered once. */
const reconnectRefreshArmed = new WeakSet<HTMLElement>();

/**
 * Nothing replays the events published while the websocket was down. If RUN_FINISHED fell into the gap the
 * chat would believe a run is still going, composer disabled and nothing to click, until a reload. So every
 * time the connection comes back the thread is fetched again and, when it moved on, the chat is rebuilt from
 * it in place, the way a stale thread already is. The draft in the composer survives.
 */
const armReconnectRefresh = (hostElement: HTMLElement): void => {
    if (reconnectRefreshArmed.has(hostElement) || typeof real === 'undefined' || !real.realtime?.onConnected) {
        return;
    }
    reconnectRefreshArmed.add(hostElement);

    // onConnected fires for the connection that is up right now as well. That first call is the present
    // connection, not a reconnect.
    let connections = 0;
    real.realtime.onConnected(() => {
        connections += 1;
        if (connections > 1) {
            void refreshAfterReconnect(hostElement);
        }
    });
};

const refreshAfterReconnect = async (hostElement: HTMLElement): Promise<void> => {
    const instance = instances.get(hostElement);
    if (!instance || instance.bootstrap.bootstrapUrl === '') {
        return;
    }

    let payload: Record<string, unknown>;
    try {
        payload = await fetchChatbotBootstrap(
            instance.bootstrap.bootstrapUrl,
            instance.bootstrap.runMetadata,
            websocketIdHeader(),
        );
    } catch {
        return;
    }
    if (instances.get(hostElement) !== instance) {
        return;
    }

    const bootstrap = normalizeBootstrap(payload as Partial<ChatbotBootstrap>);
    // A blip that missed nothing is not worth redrawing the transcript for.
    const caughtUp = bootstrap.threadId === instance.bootstrap.threadId
        && bootstrap.threadRevision === instance.bootstrap.threadRevision
        && bootstrap.runInProgress === instance.store.snapshot().running;
    if (caughtUp) {
        // The thread did not move, but a change set may have, and its pushes are not replayed either.
        let change: ReturnType<ChatStateStore['applyStatusItemEvent']> = null;
        for (const changeSet of bootstrap.pageChangeSets || []) {
            const event = chatStatusItemEventFromUnknown(changeSet);
            change = event === null ? change : instance.store.applyStatusItemEvent(event) ?? change;
        }
        if (change !== null) {
            instance.ui.applyStatusChange(change);
        }
        return;
    }

    const draft = instance.ui.getDraft();
    bootstrap.labels = mergeLabels(instance.bootstrap.labels, bootstrap.labels);
    destroyChatbotInstance(hostElement);
    const refreshed = await initChatbot(hostElement, instance.container, bootstrap);
    refreshed.ui.setDraft(draft);
};

export const destroyChatbot = (hostElement: HTMLElement): void => {
    const instance = instances.get(hostElement);
    if (instance) {
        rememberChatbotRestartSource(instance.container, {
            bootstrapUrl: instance.bootstrap.bootstrapUrl,
            runMetadata: instance.bootstrap.runMetadata,
        });
        restartLabels.set(instance.container, instance.bootstrap.labels);
    }
    runMetadataOverrides.delete(hostElement);
    destroyChatbotInstance(hostElement);
};

const destroyChatbotInstance = (hostElement: HTMLElement): void => {
    const instance = instances.get(hostElement);
    if (!instance) {
        return;
    }

    instance.agent.abortRun();
    clearRunWatchdog(instance);
    instance.unsubscribeAgUi?.();
    instance.unsubscribeStatusItems?.();
    if (instance.bootstrap.agUiTopic && typeof real !== 'undefined') {
        real.realtime?.unsubscribe?.(instance.bootstrap.agUiTopic);
    }
    instance.ui.destroy();
    instances.delete(hostElement);
};

export const resetChatbot = async (hostElement: HTMLElement, container: HTMLElement): Promise<void> => {
    const instance = instances.get(hostElement);
    if (!instance || instance.agent.isRunning || instance.ui.isChangeSetRequestPending()) {
        return;
    }
    // Changes being applied report to this chat only, so it stays until they are done.
    if (instance.ui.isApplyingChanges()) {
        instance.ui.explainNewChatWaits();
        return;
    }

    const response = await fetch(instance.bootstrap.newThreadUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...websocketIdHeader(),
        },
        body: JSON.stringify({
            threadId: instance.bootstrap.threadId,
            forwardedProps: instance.bootstrap.runMetadata,
        }),
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();
    const bootstrap = normalizeBootstrap(payload.chatbot as Partial<ChatbotBootstrap>);
    bootstrap.labels = mergeLabels(instance.bootstrap.labels, bootstrap.labels);
    destroyChatbotInstance(hostElement);
    await initChatbot(hostElement, container, bootstrap);
};

/**
 * Draws the staged changes again, for example after the Sidebar opened or closed.
 */
export const refreshChatbotChangeSets = (hostElement: HTMLElement): void => {
    instances.get(hostElement)?.ui.refreshChangeSets();
};

/**
 * Puts text into the chat box for the user to edit or send, for a control outside the chat that drafts a
 * request on their behalf.
 */
export const fillChatbotComposer = (hostElement: HTMLElement, text: string): void => {
    instances.get(hostElement)?.ui.fillComposer(text);
};

export const updateChatbotRunMetadata = (
    hostElement: HTMLElement,
    metadata: Record<string, unknown>,
): void => {
    runMetadataOverrides.set(hostElement, {...runMetadataOverrides.get(hostElement), ...metadata});
    const instance = instances.get(hostElement);
    if (!instance) {
        return;
    }

    instance.bootstrap.runMetadata = {
        ...instance.bootstrap.runMetadata,
        ...metadata,
    };
    // Staged changes are shown only on their own page, so moving to another page changes which cards show.
    instance.ui.refreshChangeSets();
};

const currentPageIdOf = (runMetadata: Record<string, unknown>): string => stringValue(runMetadata.pageId);

/**
 * Sends a message as if the user had typed it into the composer, with run metadata that applies to this one
 * run only. Returns false when the chat cannot take a message right now, so the caller can keep the text.
 */
export const submitChatbotMessage = (
    hostElement: HTMLElement,
    content: string,
    runMetadata: Record<string, unknown> = {},
): boolean => {
    const instance = instances.get(hostElement);
    if (!instance || instance.agent.isRunning || instance.ui.isPaused()) {
        return false;
    }

    // Sent from somewhere other than the composer, so the transcript is pinned here the way submitting in the
    // composer pins it.
    instance.ui.scrollToBottom();
    void runMessage(hostElement, content, runMetadata);

    return true;
};

/**
 * The state store of a chatbot that is already running on this host element, or null when there is none.
 * Reaching an existing instance is the only way to read a chat someone else initialised: the bootstrap data
 * attribute is consumed by the first init, so a second one has nothing to start from.
 */
export const getChatbotStore = (hostElement: HTMLElement): ChatStateStore | null =>
    instances.get(hostElement)?.store ?? null;

export const scrollChatbotToBottom = (hostElement: HTMLElement): void => {
    const instance = instances.get(hostElement);
    if (!instance) {
        return;
    }

    instance.ui.scrollToBottom();
    instance.ui.revealLatestChangeSet();
};

const createAgent = (bootstrap: ChatbotBootstrap): ChatbotAgent => {
    const agent: ChatbotAgent = {
        isRunning: false,
        state: bootstrap.state || {},
        abortRun(): void {
            this.isRunning = false;
        },
    };

    return agent;
};

const runMessage = async (
    hostElement: HTMLElement,
    content: string,
    extraRunMetadata: Record<string, unknown> = {},
): Promise<void> => {
    const instance = instances.get(hostElement);
    if (!instance || instance.agent.isRunning) {
        return;
    }

    if (isActivityIdle()) {
        real.window?.reloadPageOrNotifyUser?.(howspace.translate('layout_disconnected_due_to_inactivity'), false);
        return;
    }

    const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
    };

    instance.agent.isRunning = true;
    instance.waitingForWorkerRunId = null;
    const canonicalSnapshot = instance.store.snapshot();
    const canonicalThreadRevision = instance.bootstrap.threadRevision;
    const runId = crypto.randomUUID();
    // What the user had in front of them while writing. Confirming staged changes in a message approves
    // exactly these revisions and nothing staged later.
    const reviewedSets = canonicalSnapshot.statusItems
        .filter(isPageChangeSetStatusItem)
        .filter(set => set.status === 'staged' && set.pageId === currentPageIdOf(instance.bootstrap.runMetadata));
    const reviewedChangeSets = Object.fromEntries(reviewedSets.map(set => [set.id, set.revision]));
    const confirmedChanges = instance.ui.confirmedChanges(reviewedSets);
    const runMetadata = {
        ...instance.bootstrap.runMetadata,
        ...extraRunMetadata,
        observedThreadRevision: instance.bootstrap.threadRevision,
        ...(Object.keys(reviewedChangeSets).length > 0 ? {reviewedChangeSets} : {}),
        ...(Object.keys(confirmedChanges).length > 0 ? {confirmedChanges} : {}),
    };
    const optimisticRunStartedEvent: ChatAgUiEvent = {
        type: EventType.RUN_STARTED,
        threadId: instance.bootstrap.threadId,
        runId,
        input: {
            threadId: instance.bootstrap.threadId,
            runId,
            tools: [],
            context: [],
            forwardedProps: runMetadata,
            messages: [userMessage],
            state: instance.agent.state,
        },
    };
    // Show the user's message immediately, but let the server-sent RUN_STARTED advance the persisted revision.
    instance.ui.applyAgUiChange(instance.store.apply(optimisticRunStartedEvent, false));
    syncRunWatchdog(instance);
    instance.waitingForWorkerRunId = runId;
    instance.pendingRun = {snapshot: canonicalSnapshot, threadRevision: canonicalThreadRevision, content};

    try {
        const response = await fetch(instance.bootstrap.runUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...websocketIdHeader(),
            },
            body: JSON.stringify({
                threadId: instance.bootstrap.threadId,
                runId,
                tools: [],
                context: [],
                forwardedProps: runMetadata,
                messages: [userMessage],
                state: instance.agent.state,
            }),
        });

        if (!response.ok) {
            throw await createHttpError(response);
        }

        const payload = await response.json().catch(() => null);
        if (!isRecord(payload) || payload.status !== 'OK') {
            throw new Error('Unexpected chatbot run response.');
        }

        if (instance.waitingForWorkerRunId === runId) {
            instance.ui.startQueuedRunNoticeTimer();
        }
    } catch (error) {
        if (await handleThreadStaleError(hostElement, instance, content, error)) {
            return;
        }

        clearQueuedRunNotice(instance, runId);
        const payload = httpErrorPayload(error);
        if (payload === null) {
            reportClientRunError(error, instance, runId);
        }

        instance.pendingRun = null;
        rollbackOptimisticRun(instance, canonicalSnapshot, canonicalThreadRevision);
        // The credits ran out after the chat loaded. It says so the way a chat loaded then would.
        if (errorCode(payload) === AI_CREDITS_EXHAUSTED_ERROR_CODE) {
            instance.ui.pause(errorMessage(error, payload, instance.bootstrap.labels.error));
            instance.ui.setDraft(content);
            return;
        }
        applyLocalRunError(
            instance,
            errorMessage(error, payload, instance.bootstrap.labels.error),
            errorCode(payload) || 'client_run_failed',
        );
        instance.ui.setDraft(content);
    }
};

const openWorkspaceContextFileModal = (hostElement: HTMLElement, file?: File): void => {
    const instance = instances.get(hostElement);
    if (!instance || !instance.bootstrap.features.workspaceContextUpload) {
        return;
    }

    const opener = howspace?.workspace?.contextFileModal?.openAddFileModal;
    if (typeof opener !== 'function') {
        applyLocalRunError(instance, 'Context file upload is not available.', 'context_upload_unavailable');
        return;
    }

    opener({
        initialFile: file,
        chatbotUpload: {
            threadId: instance.bootstrap.threadId,
        },
        onCreated: (response: unknown) => {
            const contextFile = isRecord(response) && isRecord(response.workspace_context_file)
                ? response.workspace_context_file
                : null;
            if (contextFile !== null) {
                applyWorkspaceContextUploadStatus(instance, contextFile);
            }
        },
        onClosed: () => {
            instance.ui.focusComposer();
        },
    });
};

/**
 * Deletes an uploaded source file and reports whether the server agreed. The server pushes a removal event
 * to every open client, so the row disappears there too, and a reload no longer lists the file.
 */
const removeWorkspaceContextFile = async (contextFileId: string): Promise<boolean> => {
    if (contextFileId === '') {
        return false;
    }

    try {
        const response = await fetch(`${real.baseUri}/manager/context/${encodeURIComponent(contextFileId)}/delete`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
        return response.ok;
    } catch {
        return false;
    }
};

const applyWorkspaceContextUploadStatus = (
    instance: ChatbotInstance,
    contextFile: Record<string, unknown>,
): void => {
    const id = stringValue(contextFile.id);
    if (id === '') {
        return;
    }
    const status = workspaceContextUploadStatus(contextFile.status);
    if (status === null) {
        return;
    }

    const statusItemEvent: WorkspaceContextUploadStatusItemEvent = {
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        id,
        contextFileId: id,
        filename: stringValue(contextFile.filename),
        title: stringValue(contextFile.title),
        status,
        userVisibleError: stringValue(contextFile.user_visible_error),
    };
    const change = instance.store.applyStatusItemEvent(statusItemEvent);
    if (change !== null) {
        instance.ui.applyStatusChange(change);
    }
};

/** A hung request must still end, so the card's buttons come back. */
const CHANGE_SET_REQUEST_TIMEOUT_MS = 30_000;
const RUN_CLOSE_RETRY_DELAY_MS = 1_500;

/**
 * The card buttons call the server directly, not through the agent, and the server answers with the set as it is
 * afterwards. A failed request is sent once more with the same request id, so an apply that did reach the server
 * is not queued twice.
 */
const pageChangeSetActions = (
    hostElement: HTMLElement,
    baseUrl: string,
    labels: Record<string, string>,
): PageChangeSetActions => {
    const fetchSet = async (url: string, init: RequestInit): Promise<Response | null> => {
        try {
            return await fetch(url, {
                ...init,
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    ...websocketIdHeader(),
                },
                signal: AbortSignal.timeout(CHANGE_SET_REQUEST_TIMEOUT_MS),
            });
        } catch {
            return null;
        }
    };

    /** Shows the set the server answered with, unless New chat replaced the chat that asked. */
    const showAnswer = (sender: ChatbotInstance | undefined, payload: unknown): void => {
        const instance = instances.get(hostElement);
        if (instance === undefined || instance !== sender) {
            return;
        }
        const event = isRecord(payload) ? chatStatusItemEventFromUnknown(payload.changeSet) : null;
        if (event !== null) {
            const change = instance.store.applyStatusItemEvent(event);
            if (change !== null) {
                instance.ui.applyStatusChange(change);
            }
        }
    };

    const post = async (
        set: PageChangeSetStatusItem,
        action: string,
        body: Record<string, unknown>,
    ): Promise<string | null> => {
        const url = `${baseUrl}/${encodeURIComponent(set.id)}/${action}`;
        const sender = instances.get(hostElement);
        const send = (): Promise<Response | null> => fetchSet(url, {method: 'POST', body: JSON.stringify(body)});
        let response = await send() ?? await send();
        // The buttons come back when the reply's last event arrives, a moment before the server has closed the
        // run. One more try a little later covers that gap.
        if (response !== null && response.status === 409) {
            const payload = await response.clone().json().catch(() => null);
            if (errorCode(payload) === 'run_in_progress') {
                await new Promise(resolve => window.setTimeout(resolve, RUN_CLOSE_RETRY_DELAY_MS));
                response = await send() ?? response;
            }
        }
        if (response === null) {
            return labels.actionFailed || 'That did not work. Try again.';
        }

        const payload = await response.json().catch(() => null);
        if (instances.get(hostElement) !== sender) {
            return null;
        }
        showAnswer(sender, payload);
        if (response.ok) {
            return null;
        }

        const code = errorCode(payload);
        if (code === 'changed_since_review') {
            return labels.changedSinceReview || null;
        }
        if (code === 'run_in_progress') {
            return labels.runInProgress || null;
        }
        // A set that moved on meanwhile, applied or canceled in another tab, is shown as it is now. That is
        // the answer, not an error.
        return code === 'not_staged' || code === 'conflict' || code === 'apply_in_progress'
            ? null
            : (labels.actionFailed || null);
    };

    return {
        apply: (set, confirmed) => post(set, 'apply', {
            revision: set.revision,
            confirmed,
            requestId: crypto.randomUUID(),
            pageId: currentPageIdOf(instances.get(hostElement)?.bootstrap.runMetadata ?? {}),
        }),
        skip: (set, operationId) => post(set, 'skip', {operationId}),
        cancel: set => post(set, 'cancel', {}),
        retryPage: (set, operationId) => post(set, 'retry-page', {operationId}),
        openPage: (set, operationId) => post(set, 'open-page', {operationId}),
        dismiss: async set => {
            await fetchSet(`${baseUrl}/${encodeURIComponent(set.id)}/dismiss`, {method: 'POST', body: '{}'});
        },
        refresh: async set => {
            const sender = instances.get(hostElement);
            const response = await fetchSet(`${baseUrl}/${encodeURIComponent(set.id)}`, {method: 'GET'});
            if (response?.ok) {
                showAnswer(sender, await response.json().catch(() => null));
            }
        },
    };
};

const showConnectionLoading = (
    container: HTMLElement,
    labels: ChatbotBootstrap['labels'],
): HTMLElement => {
    container.replaceChildren();
    const loading = document.createElement('div');
    loading.className = 'howspace-ai-chat-loading';

    const status = document.createElement('span');
    status.className = 'sr-only';
    status.setAttribute('role', 'status');

    const spinner = document.createElement('span');
    spinner.className = 'howspace-ai-chat-loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const message = document.createElement('div');
    message.className = 'howspace-ai-chat-loading-message';

    loading.append(status, spinner, message);
    container.append(loading);
    // A live region is announced when its text changes, not when it is inserted with the text already in it.
    requestAnimationFrame(() => {
        status.textContent = labels.thinking || 'Loading';
    });

    return loading;
};

const showConnectionError = (
    loadingIndicator: HTMLElement,
    labels: ChatbotBootstrap['labels'],
): void => {
    const message = labels.connectionFailed || "We couldn't connect to the chat. Please reload the page and try again.";
    loadingIndicator.classList.add('is-error');
    loadingIndicator.querySelector('[role="status"]')?.remove();
    const messageElement = loadingIndicator.querySelector<HTMLElement>('.howspace-ai-chat-loading-message');
    if (messageElement) {
        // An alert is announced when its text arrives. Changing the role of an element already on the page is not.
        messageElement.setAttribute('role', 'alert');
        messageElement.textContent = message;
    }
};

const subscribeToAgUiTopic = async (topic: string | undefined, token: string | undefined): Promise<void> => {
    if (!topic) {
        return;
    }

    if (typeof real === 'undefined' || !real.realtime) {
        throw new Error('Realtime connection is not available.');
    }

    await new Promise<void>((resolve, reject) => {
        real.realtime.subscribe(topicWithToken(topic, token), (error: Error | null) => {
            if (error) {
                reject(error);
                return;
            }
            resolve();
        });
    });
};

const topicWithToken = (topic: string, token: string | undefined): string => (
    token ? `${topic}@${token}` : topic
);

const websocketIdHeader = (): Record<string, string> => {
    const connectionId = typeof real !== 'undefined'
        ? real.realtime?.uwebsocket?.getConnectionId?.()
        : '';

    return typeof connectionId === 'string' && connectionId !== ''
        ? {'X-Howspace-WebsocketId': connectionId}
        : {};
};

const listenToAgUiEvents = (instance: ChatbotInstance): (() => void) | null => {
    if (!instance.bootstrap.agUiTopic) {
        return null;
    }

    const handler = (notification: unknown): void => {
        if (!isRecord(notification)) {
            return;
        }

        const object = notification.object;
        if (!isRecord(object)) {
            return;
        }

        if (object.topic !== instance.bootstrap.agUiTopic || !isRecord(object.event)) {
            return;
        }

        const event = object.event as ChatAgUiEvent;
        markWorkerSignalReceived(
            instance,
            typeof object.runId === 'string' ? object.runId : runIdFromAgUiEvent(event),
        );
        const canonicalSnapshot = instance.store.snapshot();
        const canonicalThreadRevision = instance.bootstrap.threadRevision;
        try {
            applyAgUiEvent(
                instance,
                event,
                typeof object.threadRevision === 'number' ? object.threadRevision : null,
            );
        } catch (error) {
            reportClientRunError(error, instance, runIdFromAgUiEvent(event));
            rollbackOptimisticRun(instance, canonicalSnapshot, canonicalThreadRevision);
            applyLocalRunError(
                instance,
                errorMessage(error, null, instance.bootstrap.labels.error),
                'client_event_failed',
            );
        }
    };

    real.events.on(AG_UI_EVENT_ACTION, handler);
    return () => real.events.off(AG_UI_EVENT_ACTION, handler);
};

const listenToStatusItemUpdates = (instance: ChatbotInstance): (() => void) | null => {
    if (!instance.bootstrap.agUiTopic) {
        return null;
    }

    const handler = (notification: unknown): void => {
        if (!isRecord(notification)) {
            return;
        }

        const object = notification.object;
        if (!isRecord(object) || object.topic !== instance.bootstrap.agUiTopic) {
            return;
        }

        const statusItemEvent = chatStatusItemEventFromUnknown(object.statusItemEvent);
        if (statusItemEvent === null) {
            return;
        }

        const change = instance.store.applyStatusItemEvent(statusItemEvent);
        if (change !== null) {
            instance.ui.applyStatusChange(change);
        }
    };

    real.events.on(CHAT_STATUS_ITEM_ACTION, handler);
    return () => real.events.off(CHAT_STATUS_ITEM_ACTION, handler);
};

const isActivityIdle = (): boolean => (
    typeof howspace !== 'undefined'
        && typeof howspace.activityTracker?.isIdle === 'function'
        && howspace.activityTracker.isIdle()
);

/**
 * The revision comes from the server, on every push, and is never counted here.
 *
 * It used to be read off this store's own event count when a run finished. That holds only while the thread
 * and the browser see exactly the same events, and the AI Builder's plan panel breaks that: it writes the
 * plan through its own endpoint, which pushes a state snapshot the thread does not store. The browser then
 * believed it was one ahead, the next message was refused as stale, and the chat quietly rebuilt itself.
 * Whether the counts happened to agree came down to whether that push overtook RUN_FINISHED.
 *
 * Only forward, because the two writers are separate processes with no ordering between them. A push that
 * arrives late still carries a revision from before the run it is behind, and honouring it would undo what
 * the run already reported.
 */
const applyAgUiEvent = (
    instance: ChatbotInstance,
    event: ChatAgUiEvent,
    threadRevision: number | null,
): void => {
    if (threadRevision !== null && threadRevision > instance.bootstrap.threadRevision) {
        instance.bootstrap.threadRevision = threadRevision;
    }

    if (event.type === EventType.STATE_SNAPSHOT) {
        instance.agent.state = event.snapshot || {};
        instance.store.setState(instance.agent.state);
    }

    const change = instance.store.apply(event);
    syncRunWatchdog(instance);
    if (change !== null) {
        instance.ui.applyAgUiChange(change);
    }

    if (event.type === EventType.RUN_FINISHED || event.type === EventType.RUN_ERROR) {
        instance.agent.isRunning = false;
        // The run reached its own end, which is where it stores everything it produced.
        instance.pendingRun = null;
    }
};

const rollbackOptimisticRun = (
    instance: ChatbotInstance,
    snapshot: ChatSnapshot,
    threadRevision: number,
): void => {
    // Status items are not events and are not undone with the run, so they are carried over as they are now.
    const statusItems = instance.store.snapshot().statusItems;
    instance.store = new ChatStateStore(snapshot.events);
    statusItems.forEach(item => instance.store.applyStatusItemEvent(item));
    instance.store.setState(snapshot.state);
    instance.agent.state = snapshot.state;
    instance.agent.isRunning = false;
    instance.bootstrap.threadRevision = threadRevision;
    instance.waitingForWorkerRunId = null;
    instance.ui.renderInitial(instance.store.snapshot());
};

/**
 * Nothing on the client ends a run except the server saying so, and a worker that dies publishes neither
 * RUN_FINISHED nor RUN_ERROR. Without this the chat would animate "thinking" and hold the composer disabled
 * until the tab is reloaded, and a reload inside the run lock comes back in the same state.
 *
 * So a run that outlives the longest one the server allows is reported as the timeout the server would have
 * reported, which is a state the user already knows how to get out of. Armed from when this client learned
 * the run was going, which for a run that began before a reload is later than it really started: erring
 * towards waiting too long, never towards cutting off a run that is still alive.
 */
const RUN_WATCHDOG_GRACE_MS = 15_000;

const syncRunWatchdog = (instance: ChatbotInstance): void => {
    const running = instance.store.snapshot().running;
    if (!running || instance.bootstrap.runHardCapSeconds <= 0) {
        clearRunWatchdog(instance);
        return;
    }

    if (instance.runWatchdog !== null) {
        return;
    }

    instance.runWatchdog = window.setTimeout(() => {
        instance.runWatchdog = null;
        if (!instance.store.snapshot().running) {
            return;
        }

        abandonUnpersistedRun(instance);
    }, instance.bootstrap.runHardCapSeconds * 1000 + RUN_WATCHDOG_GRACE_MS);
};

/**
 * What to do about a run that has gone quiet for longer than one is allowed to take. Which of the two it is
 * comes from whether anything was ever heard back from it.
 *
 * Nothing heard at all means it has not started yet. The message is still on its way to being answered, so
 * nothing is undone and nothing is offered to press: saying it failed would be untrue, and sending it again
 * would ask the same thing twice. Freeing the composer is the whole point, because otherwise the chat stays
 * disabled for ever. When the run does start, its own RUN_STARTED clears this notice and re-adds the message
 * under the same id, so the chat picks up where it left off. What was kept to undo the send is kept, because
 * a run that starts late can still be lost in its turn.
 *
 * Something heard, then silence, means the run was lost along with everything it had not stored: a thread is
 * written in one go when a run ends, so neither the message nor anything streamed under it survived. Putting
 * the chat back as it was before the send is what a failed send already does, and it restores the revision
 * too, so the next message is not mistaken for a tab that fell behind. The text returns to the composer,
 * which is what the retry then sends.
 *
 * A run this client did not start has nothing to restore, which is the case after a reload mid run. There
 * the timeout is still worth reporting, for the same composer reason.
 */
const abandonUnpersistedRun = (instance: ChatbotInstance): void => {
    if (instance.waitingForWorkerRunId !== null) {
        applyLocalRunError(
            instance,
            instance.bootstrap.labels.runStillQueuedNotice || instance.bootstrap.labels.queueDelayNotice,
            'agent_run_still_queued',
        );
        return;
    }

    const pending = instance.pendingRun;
    instance.pendingRun = null;
    if (pending !== null) {
        rollbackOptimisticRun(instance, pending.snapshot, pending.threadRevision);
    }

    applyLocalRunError(instance, instance.bootstrap.labels.runTimeoutWarning, 'agent_run_abandoned');
    if (pending !== null) {
        instance.ui.setDraft(pending.content);
    }
};

const clearRunWatchdog = (instance: ChatbotInstance): void => {
    if (instance.runWatchdog !== null) {
        window.clearTimeout(instance.runWatchdog);
        instance.runWatchdog = null;
    }
};

const applyLocalRunError = (instance: ChatbotInstance, message: string, code: string): void => {
    instance.agent.isRunning = false;
    const event: ChatAgUiEvent = {
        type: EventType.RUN_ERROR,
        message,
        code,
    };
    const change = instance.store.apply(event, false);
    clearRunWatchdog(instance);
    if (change !== null) {
        instance.ui.applyAgUiChange(change);
    }
};

const markWorkerSignalReceived = (instance: ChatbotInstance, runId: string): void => {
    if (instance.waitingForWorkerRunId !== runId) {
        return;
    }

    // The websocket RUN_STARTED event is emitted by AgentRunner after a worker has
    // picked up the queued run. Any later event for the same run implies that too.
    //
    // The client's ceiling measures the run, so it starts where the worker's own budget starts rather than
    // where the queueing did. Only this first signal re-arms it, because clearQueuedRunNotice drops the run
    // being waited for, so a run that keeps streaming cannot push its deadline out for ever.
    clearRunWatchdog(instance);
    clearQueuedRunNotice(instance, runId);
    syncRunWatchdog(instance);
};

const clearQueuedRunNotice = (instance: ChatbotInstance, runId: string): void => {
    if (instance.waitingForWorkerRunId !== runId) {
        return;
    }

    instance.waitingForWorkerRunId = null;
    instance.ui.clearQueuedRunNotice();
};

const createHttpError = async (response: Response): Promise<Error & {payload?: unknown}> => {
    const error = new Error(`HTTP ${response.status}`) as Error & {payload?: unknown};
    error.payload = await response.json().catch(() => null);
    return error;
};

const handleThreadStaleError = async (
    hostElement: HTMLElement,
    instance: ChatbotInstance,
    draft: string,
    error: unknown,
): Promise<boolean> => {
    const payload = httpErrorPayload(error);
    if (errorCode(payload) !== 'thread_stale' || !isRecord(payload) || !isRecord(payload.chatbot)) {
        return false;
    }

    const bootstrap = normalizeBootstrap(payload.chatbot as Partial<ChatbotBootstrap>);
    bootstrap.labels = mergeLabels(instance.bootstrap.labels, bootstrap.labels);
    destroyChatbotInstance(hostElement);
    const refreshed = await initChatbot(hostElement, instance.container, bootstrap);
    refreshed.ui.setDraft(draft);
    const staleEvent: ChatAgUiEvent = {
        type: EventType.RUN_ERROR,
        message: refreshed.bootstrap.labels.threadStaleWarning || errorMessage(error, payload, refreshed.bootstrap.labels.error),
        code: 'thread_stale',
    };
    refreshed.ui.applyAgUiChange(refreshed.store.apply(staleEvent, false));
    return true;
};

const reportClientRunError = (error: unknown, instance: ChatbotInstance, runId: string): void => {
    const originalError = normalizeError(error);
    const message = `AI chatbot client run failed: ${originalError.message}`;
    const loggedError = new Error(message);
    const context = {
        agentId: instance.bootstrap.agentId,
        threadTypeId: instance.bootstrap.threadTypeId,
        threadId: instance.bootstrap.threadId,
        runId,
    };

    loggedError.stack = [
        `${loggedError.name}: ${message}`,
        originalError.stack || '',
        '',
        'Chatbot context:',
        JSON.stringify(context, null, 2),
    ].filter(Boolean).join('\n');

    const errorUrl = typeof real !== 'undefined' && real.cdnUri
        ? `${real.cdnUri}/assets/ts/howspace/ai/chatbot.ts`
        : window.location.href;

    if (typeof real !== 'undefined' && real.errorlogger?.sendErrorToServer) {
        real.errorlogger.sendErrorToServer(message, errorUrl, 1, 1, loggedError);
        return;
    }

    if (typeof window.onerror === 'function') {
        window.onerror(message, errorUrl, 1, 1, loggedError);
        return;
    }

    console.error(message, error);
};

const normalizeError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === 'string') {
        return new Error(error);
    }

    try {
        return new Error(JSON.stringify(error));
    } catch {
        return new Error(String(error));
    }
};

const httpErrorPayload = (error: unknown): unknown => (
    isRecord(error) && 'payload' in error ? error.payload : null
);

const errorCode = (payload: unknown): string | null => (
    isRecord(payload) && typeof payload.code === 'string' ? payload.code : null
);

const errorMessage = (error: unknown, payload: unknown, fallback: string): string => {
    if (isRecord(payload) && typeof payload.error === 'string') {
        return payload.error;
    }

    return fallback || 'Sorry, something went wrong.';
};

const runIdFromAgUiEvent = (event: ChatAgUiEvent): string => {
    return isRecord(event) && typeof event.runId === 'string' ? event.runId : 'unknown';
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);

const stringValue = (value: unknown): string => (
    typeof value === 'string' ? value : ''
);

const mergeLabels = (
    previous: ChatbotBootstrap['labels'],
    next: ChatbotBootstrap['labels'],
): ChatbotBootstrap['labels'] => {
    const labels = {...previous, ...next};
    Object.entries(labels).forEach(([key, value]) => {
        if (!value) {
            labels[key as keyof ChatbotBootstrap['labels']] = previous[key as keyof ChatbotBootstrap['labels']];
        }
    });
    return labels;
};

const normalizeBootstrap = (bootstrap: Partial<ChatbotBootstrap>): ChatbotBootstrap => ({
    chatbotId: bootstrap.chatbotId || '',
    agentId: bootstrap.agentId || '',
    threadTypeId: bootstrap.threadTypeId || '',
    threadId: bootstrap.threadId || '',
    threadRevision: bootstrap.threadRevision ?? 0,
    runUrl: bootstrap.runUrl || '',
    newThreadUrl: bootstrap.newThreadUrl || '',
    bootstrapUrl: bootstrap.bootstrapUrl || '',
    runInProgress: bootstrap.runInProgress === true,
    runHardCapSeconds: Number(bootstrap.runHardCapSeconds) > 0 ? Number(bootstrap.runHardCapSeconds) : 180,
    events: bootstrap.events || [],
    state: bootstrap.state || {},
    runMetadata: bootstrap.runMetadata || {},
    agUiTopic: bootstrap.agUiTopic || '',
    agUiTopicToken: bootstrap.agUiTopicToken || '',
    model: {
        mismatch: bootstrap.model?.mismatch ?? false,
    },
    labels: {
        placeholder: bootstrap.labels?.placeholder || '',
        send: bootstrap.labels?.send || '',
        emptyState: bootstrap.labels?.emptyState || '',
        thinking: bootstrap.labels?.thinking || '',
        thoughtFor: bootstrap.labels?.thoughtFor || '',
        fewSeconds: bootstrap.labels?.fewSeconds || '',
        seconds: bootstrap.labels?.seconds || '',
        copy: bootstrap.labels?.copy || '',
        close: bootstrap.labels?.close || '',
        expandStatus: bootstrap.labels?.expandStatus || '',
        collapseStatus: bootstrap.labels?.collapseStatus || '',
        toolCalls: bootstrap.labels?.toolCalls || '',
        toolStatusRunning: bootstrap.labels?.toolStatusRunning || '',
        toolStatusDone: bootstrap.labels?.toolStatusDone || '',
        toolStatusError: bootstrap.labels?.toolStatusError || '',
        toolArguments: bootstrap.labels?.toolArguments || '',
        toolResult: bootstrap.labels?.toolResult || '',
        modelChangedWarning: bootstrap.labels?.modelChangedWarning || '',
        threadStaleWarning: bootstrap.labels?.threadStaleWarning || '',
        runTimeoutWarning: bootstrap.labels?.runTimeoutWarning || '',
        inputContextTooLargeWarning: bootstrap.labels?.inputContextTooLargeWarning || '',
        queueDelayNotice: bootstrap.labels?.queueDelayNotice || '',
        runStillQueuedNotice: bootstrap.labels?.runStillQueuedNotice || '',
        thinkingWords: bootstrap.labels?.thinkingWords || '',
        stillWorking: bootstrap.labels?.stillWorking || '',
        connectionFailed: bootstrap.labels?.connectionFailed || '',
        error: bootstrap.labels?.error || '',
        tryAgain: bootstrap.labels?.tryAgain || '',
        addContextFile: bootstrap.labels?.addContextFile || '',
        contextFileQueued: bootstrap.labels?.contextFileQueued || '',
        contextFileProcessing: bootstrap.labels?.contextFileProcessing || '',
        contextFileReady: bootstrap.labels?.contextFileReady || '',
        contextFileFailed: bootstrap.labels?.contextFileFailed || '',
        contextFileRemove: bootstrap.labels?.contextFileRemove || '',
        contextFileRemoveConfirm: bootstrap.labels?.contextFileRemoveConfirm || '',
        contextFileRemoveConfirmAction: bootstrap.labels?.contextFileRemoveConfirmAction || '',
        contextFileKeep: bootstrap.labels?.contextFileKeep || '',
        contextFileRemoveFailed: bootstrap.labels?.contextFileRemoveFailed || '',
        contextFileGeneratePageAction: bootstrap.labels?.contextFileGeneratePageAction || '',
        contextFileCreateWidgetAction: bootstrap.labels?.contextFileCreateWidgetAction || '',
        contextFileMaxSourceFileSize: bootstrap.labels?.contextFileMaxSourceFileSize || '',
        contextFileMaxCreatedAssetCount: bootstrap.labels?.contextFileMaxCreatedAssetCount || '',
        contextFileMaxCreatedAssetFileSize: bootstrap.labels?.contextFileMaxCreatedAssetFileSize || '',
        composerNote: bootstrap.labels?.composerNote || '',
        emptyStateDescription: bootstrap.labels?.emptyStateDescription || '',
    },
    features: {
        showReasoning: bootstrap.features?.showReasoning ?? true,
        showToolCalls: bootstrap.features?.showToolCalls ?? true,
        showFriendlyToolStatus: bootstrap.features?.showFriendlyToolStatus ?? false,
        workspaceContextUpload: bootstrap.features?.workspaceContextUpload ?? false,
        contextFileQuickActions: bootstrap.features?.contextFileQuickActions ?? false,
    },
    toolLabels: normalizeStringRecord(bootstrap.toolLabels),
    openingQuestion: bootstrap.openingQuestion || '',
    aiCreditsExhaustedNotice: bootstrap.aiCreditsExhaustedNotice || '',
    workspaceContextFiles: Array.isArray(bootstrap.workspaceContextFiles)
        ? bootstrap.workspaceContextFiles
        : [],
    pageChangeSets: Array.isArray(bootstrap.pageChangeSets) ? bootstrap.pageChangeSets : [],
    pageChangeSetLabels: normalizeStringRecord(bootstrap.pageChangeSetLabels),
    pageChangeSetUrl: bootstrap.pageChangeSetUrl || '',
});

const normalizeStringRecord = (value: unknown): Record<string, string> => {
    if (!isRecord(value)) {
        return {};
    }

    const result: Record<string, string> = {};
    Object.entries(value).forEach(([key, entry]) => {
        if (typeof entry === 'string') {
            result[key] = entry;
        }
    });
    return result;
};
